/**
 * Field-level access control registry (FieldAccessConfig enforcement).
 *
 * Builds a map of CASL deny rules from `FieldDefinition.access` across all
 * registered modules. Unlike `casl.permissions[role].inverted`, these rules
 * bypass the superuser gate — an empty `allowedRoles` array blocks ADMIN too.
 *
 * See ability.factory.ts for consumption (`setFieldDenyRules`, `applyFieldDenyRules`).
 */

import type { FieldDefinition } from '@gzl10/nexus-sdk'
import type { RegisteredModule } from '@kernel/engine/module-store.js'

/**
 * Runtime deny rule derived from `FieldAccessConfig`.
 *
 * Semantics:
 * - `allowedRoles: []` → universal deny absolute (nobody, not even ADMIN)
 * - `allowedRoles: ['ADMIN']` → only ADMIN can perform action (allowlist)
 *
 * Role matching is case-sensitive against `roleNames` passed to `defineAbilityFor`.
 */
export interface FieldDenyRule {
  field: string
  action: 'read' | 'create' | 'update'
  /** Roles exempt from this deny. Empty = universal deny absolute. */
  allowedRoles: string[]
}

/**
 * Builds the field deny registry from all modules' entity definitions.
 *
 * Traverses `module.definitions[].fields[].access` and produces one
 * `FieldDenyRule` per declared action (`read`, `create`, `update`).
 *
 * Entities without `casl.subject` are skipped (cannot enforce without subject).
 * Fields without `access` or with all actions undefined produce no rules.
 */
export function buildFieldDenyRegistry(
  modules: RegisteredModule[]
): Map<string, FieldDenyRule[]> {
  const registry = new Map<string, FieldDenyRule[]>()
  const actions = ['read', 'create', 'update'] as const

  for (const mod of modules) {
    for (const def of mod.definitions ?? []) {
      const casl = (def as { casl?: { subject?: string } }).casl
      const subject = casl?.subject
      if (!subject) continue

      const fields = (def as { fields?: Record<string, FieldDefinition> }).fields ?? {}
      const rules: FieldDenyRule[] = []

      for (const [fieldName, field] of Object.entries(fields)) {
        const access = field.access
        if (!access) continue

        for (const action of actions) {
          const allowedRoles = access[action]
          if (allowedRoles === undefined) continue // no restriction declared
          rules.push({ field: fieldName, action, allowedRoles })
        }
      }

      if (rules.length > 0) {
        // Merge with existing (multiple modules could target same subject)
        const existing = registry.get(subject) ?? []
        registry.set(subject, [...existing, ...rules])
      }
    }
  }

  return registry
}
